import { isChunkedLiveDictation } from '@/app-shell/flags/live-dictate-transcript-flag';
import {
  pollLiveTranscripts,
  type QuerySessionLiveTranscripts,
} from '@/hooks/query/useQueryLiveTranscriptBySessionId';
import { updateT11nTranscriptCache } from '@/hooks/query/useQueryT11nTranscriptBySessionId';
import { deserializeBlob, throwIfAborted } from '@/lib/audio/utils';
import { getClientRenderedChunk } from '@/lib/SessionRecordingProvider/getClientRenderedChunk';
import {
  t11nClientV2,
  type TranscriptionResult,
} from '@/lib/SessionRecordingProvider/t11nClient';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import {
  type Format,
  type RecordingUploadTransport,
  type SessionChunk,
} from '@heidi/capture/contracts';
import {
  GET_LIVE_TRANSCRIPT_BY_SESSION_ID,
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import { AxiosError } from 'axios';
import { get, round, sortBy } from 'lodash-es';
import { update } from '../utils/update';
import { uploadChunkV3 } from './chunkUploadV3';
import { observeNoSpeechStartupHttpChunk } from './noSpeechStartupWarning';

interface RejectionContext {
  recordingId?: string;
  index: number;
  duration: number;
  size: number;
  service: 'v1' | 'v2';
}

const handleUploadError = (err: unknown, context: RejectionContext) => {
  if (err instanceof AxiosError) {
    // Handle 422 and FileTooSmall errors gracefully without retrying
    if (
      err.response?.status === 422 ||
      get(err.response?.data, 'code') === 'FileTooSmall'
    ) {
      // The caller marks the chunk uploaded (and may delete the blob), so a
      // server-rejected chunk must at least be visible in telemetry. Carry the
      // chunk's identity and the backend's structured `code` — never its raw
      // message, which could echo request or clinical context into telemetry.
      const rejectionCode: unknown = get(err.response?.data, 'code');
      logger.warn({
        event_type: 'UPLOAD_CHUNK_REJECTION_SWALLOWED',
        details: {
          status: err.response?.status,
          code: typeof rejectionCode === 'string' ? rejectionCode : undefined,
          ...context,
        },
      });
      return;
    }
  }

  throw err;
};

const isLiveDictationSession = (sessionId: string) =>
  isChunkedLiveDictation(
    reactQueryClient.getQueryData<QuerySessionResponse>([
      GET_SESSION_BY_ID,
      { sessionId },
    ])?.transcribe_mode
  );

const liveTranscriptRefreshes = new Map<string, { again: boolean }>();

// One read at a time per session, then one more if a chunk landed meanwhile.
// `pollLiveTranscripts` invalidates three times per chunk and each invalidation
// cancels the read in flight; with a chunk every few seconds, and a read that
// takes seconds while a recording is open, none of them would ever finish.
const refreshLiveTranscript = async (sessionId: string) => {
  const running = liveTranscriptRefreshes.get(sessionId);
  if (running) {
    running.again = true;
    return;
  }
  const refresh = { again: false };
  liveTranscriptRefreshes.set(sessionId, refresh);
  const queryKey = [GET_LIVE_TRANSCRIPT_BY_SESSION_ID, { sessionId }];
  try {
    do {
      // A read already in flight started before this chunk was transcribed.
      const joinsOlderRead = reactQueryClient.isFetching({ queryKey }) > 0;
      refresh.again = false;
      await reactQueryClient.refetchQueries({ queryKey }, { cancelRefetch: false });
      if (joinsOlderRead) refresh.again = true;
    } while (refresh.again);
  } finally {
    liveTranscriptRefreshes.delete(sessionId);
  }
};

type LiveChunk = NonNullable<
  QuerySessionLiveTranscripts['live']
>[number]['chunks'][number];

interface TranscribeChunkResponse {
  is_success: boolean;
  chunk?: LiveChunk | null;
}

// Puts the chunk the upload returned into the live transcript, so it shows
// without a read. False when the recording has not been read into the cache
// yet; the caller reads it instead.
const writeLiveChunk = (sessionId: string, recordingId: string, chunk: LiveChunk) => {
  let written = false;
  reactQueryClient.setQueryData<QuerySessionLiveTranscripts>(
    [GET_LIVE_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
    (old) => {
      const recording = old?.live?.find((r) => r.id === recordingId);
      if (!old?.live || !recording) return old;
      written = true;
      const chunks = sortBy(
        [...recording.chunks.filter((c) => c.id !== chunk.id), chunk],
        'id'
      );
      return {
        ...old,
        live: old.live.map((r) => (r === recording ? { ...r, chunks } : r)),
      };
    }
  );
  return written;
};

const uploadChunkLegacy = async (
  { index, recordingId, ...chunk }: SessionChunk,
  format: Format,
  signal?: AbortSignal
) => {
  if (!recordingId) return;
  const formData = new FormData();
  formData.append('file', deserializeBlob(chunk.blob)!, `chunk-${index + 1}.${format}`);
  formData.append('index', String(index));
  formData.append('duration', String(chunk.duration));
  const isLiveDictation = isLiveDictationSession(chunk.sessionId);
  if (isLiveDictation) formData.append('include_chunk', 'true');

  try {
    const url = `/sessions/${chunk.sessionId}/restful-segment-transcription/${recordingId}:transcribe`;
    const response = await (signal
      ? scribeHttpV2.post<TranscribeChunkResponse>(url, formData, { signal })
      : scribeHttpV2.post<TranscribeChunkResponse>(url, formData));

    throwIfAborted(signal);
    if (!isLiveDictation) {
      void pollLiveTranscripts(chunk.sessionId);
      return;
    }
    const transcribed = response?.data?.chunk;
    if (!transcribed || !writeLiveChunk(chunk.sessionId, recordingId, transcribed)) {
      void refreshLiveTranscript(chunk.sessionId);
    }
  } catch (err) {
    handleUploadError(err, {
      recordingId,
      index,
      duration: chunk.duration,
      size: chunk.size,
      service: 'v1',
    });
  }
};

export const uploadChunk = async (
  chunk: SessionChunk,
  format: Format,
  useTranscriptionServiceV2: boolean,
  languages: string[] = ['en'],
  signal?: AbortSignal,
  uploadTransport?: RecordingUploadTransport
) => {
  // The pinned transport outranks every flag: a temporal recording keeps its
  // delivery even after a kill-switch flip or a legacy-rollback drain.
  if (uploadTransport?.kind === 'temporal') {
    await uploadChunkV3(chunk, format, uploadTransport, languages, signal);
    return;
  }
  // Exhaustiveness: a future transport kind must be routed deliberately, not
  // silently fall through to HTTP — that is the mixing the design forbids.
  uploadTransport satisfies { kind: 'http' } | undefined;

  if (!useTranscriptionServiceV2) {
    await uploadChunkLegacy(chunk, format, signal);
    return;
  }

  const { recordingId, index, duration, sessionId, blob: blobSerialized } = chunk;
  if (!blobSerialized) throw new Error();
  if (!recordingId) return;
  const formData = new FormData();
  const blob = deserializeBlob(blobSerialized)!;
  formData.append('file', blob, `chunk-${index + 1}.${format}`);
  formData.append('chunk_index', String(index));
  formData.append('duration', String(round(duration * 1000)));
  formData.append('recording_id', recordingId);
  formData.append('chunk_produced_at', new Date(chunk.producedAt).toISOString());
  languages?.forEach((lang) => formData.append('languages[]', lang));

  try {
    const url = `/${sessionId}/transcribe-chunk`;
    const { data } = await (signal
      ? t11nClientV2.post<TranscriptionResult>(url, formData, { signal })
      : t11nClientV2.post<TranscriptionResult>(url, formData));
    throwIfAborted(signal);
    optimisticallyUpdateCache(chunk, data);
    observeNoSpeechStartupHttpChunk(chunk, data.text ?? '');
  } catch (err) {
    handleUploadError(err, {
      recordingId,
      index,
      duration,
      size: chunk.size,
      service: 'v2',
    });
  }
};

const optimisticallyUpdateCache = (chunk: SessionChunk, data: TranscriptionResult) => {
  updateT11nTranscriptCache(chunk.sessionId, (state) => {
    const hasRecording = state.recordings.some(
      (r) => r.recording_id === chunk.recordingId
    );

    if (!hasRecording) {
      const newChunk = getClientRenderedChunk(chunk, data, state, undefined);
      return {
        ...state,
        recordings: [
          ...state.recordings,
          {
            recording_id: chunk.recordingId,
            start_date: new Date(chunk.producedAt).toISOString(),
            chunks: [newChunk],
            issues: [],
          },
        ],
      };
    }

    return {
      ...state,
      recordings: update(
        state.recordings,
        'recording_id',
        chunk.recordingId,
        (recording) => {
          const newChunk = getClientRenderedChunk(chunk, data, state, recording);

          return {
            ...recording,
            chunks: sortBy([...recording.chunks, newChunk], (i) => i.index),
          };
        }
      ),
    };
  });
};
